import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress
} from '@mui/material';
import {
  Close,
  Article,
  Description
} from '@mui/icons-material';
import { generateCertificateOfAccreditation } from './CertificateGenerator';
import { generateScopeOfAccreditation } from './ScopeGenerator';
import { generateSCFAnnexure } from './SCFAnnexureGenerator';

const CertificateDialog = ({ open, onClose, certificateData, scopeData, scfData, loading = false }) => {
  const [certificateLoading, setCertificateLoading] = useState(false);
  const [scopeLoading, setScopeLoading] = useState(false);
  const [scfLoading, setScfLoading] = useState(false);
  
  const handleCertificateOfAccreditation = async () => {
    try {
      setCertificateLoading(true);
      // Generate Certificate of Accreditation PDF
      await generateCertificateOfAccreditation(certificateData);
      setCertificateLoading(false);
      // Optionally close dialog after successful generation
      // onClose();
    } catch (error) {
      console.error('Error generating certificate:', error);
      setCertificateLoading(false);
      alert(`Error generating certificate: ${error.message || 'Please check if certificate images exist in public/certificate_images/ folder'}`);
    }
  };

  const handleScopeOfAccreditation = async () => {
    try {
      setScopeLoading(true);
      // Generate Scope of Accreditation PDF
      await generateScopeOfAccreditation(scopeData);
      setScopeLoading(false);
      // Optionally close dialog after successful generation
      // onClose();
    } catch (error) {
      console.error('Error generating scope:', error);
      setScopeLoading(false);
      alert(`Error generating scope: ${error.message || 'Please try again'}`);
    }
  };

  const handleSCFAnnexure = async () => {
    try {
      setScfLoading(true);
      // Generate SCF Annexure PDF
      await generateSCFAnnexure(certificateData, scfData);
      setScfLoading(false);
      // Optionally close dialog after successful generation
      // onClose();
    } catch (error) {
      console.error('Error generating SCF Annexure:', error);
      setScfLoading(false);
      alert(`Error generating SCF Annexure: ${error.message || 'Please try again'}`);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" component="div">
            Download Certificate
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box sx={{ py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              Loading scope data from API...
            </Typography>
          </Box>
        ) : (
          <Box sx={{ py: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Select the document you want to download:
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Certificate of Accreditation Button */}
              <Button
                variant="outlined"
                size="large"
                startIcon={certificateLoading ? <CircularProgress size={20} /> : <Article />}
                onClick={handleCertificateOfAccreditation}
                disabled={certificateLoading || scopeLoading}
                sx={{
                  py: 2,
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  borderColor: '#1976d2',
                  color: '#1976d2',
                  '&:hover': {
                    borderColor: '#1565c0',
                    backgroundColor: 'rgba(25, 118, 210, 0.04)'
                  }
                }}
              >
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Certificate of Accreditation
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {certificateLoading ? 'Generating certificate...' : 'Download the official certificate'}
                  </Typography>
                </Box>
              </Button>

              {/* Scope of Accreditation Button */}
              <Button
                variant="outlined"
                size="large"
                startIcon={scopeLoading ? <CircularProgress size={20} /> : <Description />}
                onClick={handleScopeOfAccreditation}
                disabled={scopeLoading || !scopeData?.records || scopeData.records.length === 0}
                sx={{
                  py: 2,
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  borderColor: '#2e7d32',
                  color: '#2e7d32',
                  '&:hover': {
                    borderColor: '#1b5e20',
                    backgroundColor: 'rgba(46, 125, 50, 0.04)'
                  }
                }}
              >
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Scope of Accreditation
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {scopeLoading ? 'Generating scope...' : 
                     !scopeData?.records || scopeData.records.length === 0 ? 
                     'No scope data available' : 
                     `Download the scope details (${scopeData.records.length} records)`}
                  </Typography>
                </Box>
              </Button>

              {/* SCF Annexure Button */}
              <Button
                variant="outlined"
                size="large"
                startIcon={scfLoading ? <CircularProgress size={20} /> : <Description />}
                onClick={handleSCFAnnexure}
                disabled={scfLoading || !scfData || scfData.length === 0}
                sx={{
                  py: 2,
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  borderColor: "#ed6c02",
                  color: "#ed6c02",
                  "&:hover": {
                  borderColor: "#e65100",
                    backgroundColor: 'rgba(46, 125, 50, 0.04)'
                  }
                }}
              >
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    SCF Annexure
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {scfLoading ? 'Generating SCF Annexure...' : 
                     !scfData || scfData.length === 0 ? 
                     'No SCF data available' : 
                     `Download the SCF Annexure (${scfData.length} records)`}
                  </Typography>
                </Box>
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CertificateDialog;
